import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useDeleteStoryMutation } from "Api/Mutations/Stories/useDeleteStoryMutation";
import { usePublishStoryMutation } from "Api/Mutations/Stories/usePublishStoryMutation";
import { useUnpublishStoryMutation } from "Api/Mutations/Stories/useUnpublishStoryMutation";
import { storiesKeys } from "Api/Queries/Stories/useStoriesQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { AdminStoriesTable } from "Components/Stories/Management/AdminStoriesTable";
import { MerchantStoriesGrid } from "Components/Stories/Management/MerchantStoriesGrid";
import { StoryPreviewDialog } from "Components/Stories/Management/StoryPreviewDialog";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { StoryListItemDto } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { Link } from "Navigation/Link";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import {
  isStoryNotLiveError,
  isStoryPostLimitError,
} from "Utils/Stories/storyErrors";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Stories.Management;

/** Story management for authors: Admin gets the cross-author table, Merchant the card grid. */
export const StoriesPage = () => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const user = useLoggedInUser();
  const isAdmin = user?.accessRoleCode === AccessRoleCodes.Admin;

  const [previewPublicID, setPreviewPublicID] = useState<string | null>(null);
  const [storyToDelete, setStoryToDelete] = useState<StoryListItemDto | null>(
    null,
  );
  const [storyToUnpublish, setStoryToUnpublish] =
    useState<StoryListItemDto | null>(null);

  const { mutate: publishStory, isPending: isPublishing } =
    usePublishStoryMutation({
      onSuccess: () => toast.success(t(RESOURCES.Messages.PublishSuccess)),
      onError: (error) =>
        toast.error(
          t(
            isStoryPostLimitError(error)
              ? Resources.Stories.PostStory.LimitExceeded
              : RESOURCES.Messages.PublishError,
          ),
        ),
    });

  const { mutate: unpublishStory, isPending: isUnpublishing } =
    useUnpublishStoryMutation({
      onSuccess: () => toast.success(t(RESOURCES.Messages.UnpublishSuccess)),
      onError: (error) => {
        // The story expired (or was taken down elsewhere) between render and click: the client's
        // view was stale, not the action wrong — resync the list and say so neutrally.
        if (isStoryNotLiveError(error)) {
          queryClient.invalidateQueries({ queryKey: storiesKeys.lists() });
          toast.info(t(RESOURCES.Messages.NotLiveAnymore));
          return;
        }
        toast.error(t(RESOURCES.Messages.UnpublishError));
      },
    });

  const { mutate: deleteStory, isPending: isDeleting } = useDeleteStoryMutation(
    {
      onSuccess: () => toast.success(t(RESOURCES.Messages.DeleteSuccess)),
      onError: () => toast.error(t(RESOURCES.Messages.DeleteError)),
    },
  );

  const isPending = isPublishing || isUnpublishing || isDeleting;

  const onPreview = useCallback(
    (story: StoryListItemDto) => setPreviewPublicID(story.publicID),
    [],
  );
  const onPublish = useCallback(
    (story: StoryListItemDto) => publishStory(story.publicID),
    [publishStory],
  );
  const onDelete = useCallback(
    (story: StoryListItemDto) => setStoryToDelete(story),
    [],
  );

  const onUnpublish = useCallback(
    (story: StoryListItemDto) => setStoryToUnpublish(story),
    [],
  );

  const handleUnpublishResult = (wasConfirmed: boolean) => {
    if (wasConfirmed && storyToUnpublish) {
      unpublishStory(storyToUnpublish.publicID);
    }
    setStoryToUnpublish(null);
  };

  const handleDeleteResult = (wasConfirmed: boolean) => {
    if (wasConfirmed && storyToDelete) {
      deleteStory(storyToDelete.publicID);
    }
    setStoryToDelete(null);
  };

  return (
    <AuthenticatedLayout title={t(RESOURCES.Title)}>
      <StyledPageWrapper maxWidth={isAdmin ? "unset !important" : undefined}>
        <Stack gap={3}>
          <Box>
            <Button
              component={Link}
              to={appUrl("admin-stories-create")}
              variant="contained"
              startIcon={<AddIcon />}
            >
              {t(RESOURCES.CreateButton)}
            </Button>
          </Box>

          {isAdmin ? (
            <AdminStoriesTable
              isPending={isPending}
              onPreview={onPreview}
              onPublish={onPublish}
              onUnpublish={onUnpublish}
              onDelete={onDelete}
            />
          ) : (
            <MerchantStoriesGrid
              isPending={isPending}
              onPreview={onPreview}
              onPublish={onPublish}
              onUnpublish={onUnpublish}
              onDelete={onDelete}
            />
          )}
        </Stack>

        {previewPublicID && (
          <StoryPreviewDialog
            publicID={previewPublicID}
            onClose={() => setPreviewPublicID(null)}
          />
        )}

        <ConfirmDialog
          isOpen={storyToUnpublish !== null}
          handleResult={handleUnpublishResult}
          modalTitle={t(RESOURCES.Messages.UnpublishConfirmTitle)}
          modalContent={
            <Typography>{t(RESOURCES.Messages.UnpublishConfirm)}</Typography>
          }
          confirmButtonText={t(RESOURCES.Table.Unpublish)}
          isDestructive
        />

        <ConfirmDialog
          isOpen={storyToDelete !== null}
          handleResult={handleDeleteResult}
          modalTitle={t(RESOURCES.Messages.DeleteConfirmTitle)}
          modalContent={
            <Typography>{t(RESOURCES.Messages.DeleteConfirm)}</Typography>
          }
          confirmButtonText={t(Resources.Common.Delete)}
          isDestructive
        />
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default StoriesPage;
